import { useInputValidator } from 'modules/hooks/validationHooks/useInputValidator';
import { TextareaHTMLAttributes, useEffect, useMemo, useState } from 'react';
import { ValidationType } from 'types/ValidationType';
import { Flex } from '../flex';
import { StyledTextarea, TextareaError, TextareaWrapper } from './styled';

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  placeholderText?: string;
  required?: boolean;
  name?: string;
  defaultValue?: string | number | readonly string[];
  validators?: ValidationType[];
  validate?: boolean;
  errorText?: string;
  className?: string;
  maxLength?: number;
}

export const Textarea: React.FC<TextareaProps> = ({
  placeholderText,
  required,
  name,
  defaultValue,
  validators,
  validate,
  errorText,
  className,
  maxLength,
  ...props
}) => {
  const validateField = useInputValidator();
  const [value, setValue] = useState<typeof defaultValue>('');
  const [validationError, setValidationError] = useState<string | undefined>();
  const [lastErrorText, setLastErrorText] = useState<string | undefined>(
    errorText,
  );
  const [showError, setShowError] = useState<boolean>(false);

  useEffect(() => {
    const valError = validators
      ? validateField(value as string, validators)
      : undefined;
    setValidationError(valError);

    let error = errorText;
    if (validate && valError) {
      error = valError;
    }
    setLastErrorText(error);
    setShowError(error !== undefined && error.length > 0);
  }, [
    errorText,
    lastErrorText,
    showError,
    validate,
    validators,
    value,
    validateField,
  ]);

  useEffect(() => {
    defaultValue !== undefined && setValue(defaultValue);
  }, [defaultValue]);

  const isEditable = useMemo(() => {
    if (!(props.disabled || props.readOnly)) {
      return true;
    }
    return false;
  }, [props.disabled, props.readOnly]);

  return (
    <TextareaWrapper className={className}>
      <Flex justifyContent="center" width="100%">
        <StyledTextarea
          name={name}
          required={required}
          placeholder={placeholderText}
          className={
            validationError && validators ? 'form-input-not-valid' : ''
          }
          onChange={(e) => {
            setValue(e.target.value);
          }}
          hasError={showError}
          maxLength={maxLength}
          {...props}
        />
      </Flex>
      <TextareaError hasError={showError && isEditable}>
        <p>{lastErrorText}</p>
      </TextareaError>
    </TextareaWrapper>
  );
};
